import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BarChart3,
  Eye,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import {
  assignAdminRole,
  createAdminRole,
  deleteAdminRole,
  getActiveAdminRoles,
  getAdminModules,
  getAdminRoles,
  getRoleAnalytics,
  getUsersByAdminRole,
  getUsersByCategory,
  unassignAdminRole,
  updateAdminRole,
} from '../Services/AdminRoleService';
import {
  AdminModuleDef,
  AdminRole,
  AdminRolePayload,
  AdminUserReference,
  RoleAssignmentAnalytics,
  UserCategory,
} from '../Types/AdminRoleType';

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';
const badgeBase =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider';

const emptyForm: AdminRolePayload = {
  name: '',
  description: '',
  privileges: [],
  status: 'ACTIVE',
};

const CATEGORY_LABELS: Record<UserCategory, string> = {
  CANDIDATE: 'Candidates',
  RECRUITER: 'Recruiters',
  ADMIN: 'Admins',
};

function RoleAssignmentComponent() {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [modules, setModules] = useState<AdminModuleDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'roles' | 'assign' | 'analytics'>('roles');

  // Assign users tab
  const [assignCategory, setAssignCategory] = useState<UserCategory>('CANDIDATE');
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryUsers, setCategoryUsers] = useState<AdminUserReference[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  // Analytics tab
  const [analytics, setAnalytics] = useState<RoleAssignmentAnalytics[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRole, setAnalyticsRole] = useState<AdminRole | null>(null);
  const [analyticsRoleUsers, setAnalyticsRoleUsers] = useState<AdminUserReference[]>([]);
  const [analyticsRoleSearch, setAnalyticsRoleSearch] = useState('');
  const [analyticsRoleLoading, setAnalyticsRoleLoading] = useState(false);

  // Role form
  const [formMode, setFormMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [form, setForm] = useState<AdminRolePayload>({ ...emptyForm });
  const [selectedPrivileges, setSelectedPrivileges] = useState<Set<string>>(new Set());
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [viewRole, setViewRole] = useState<AdminRole | null>(null);

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const loadRoles = async () => {
    setLoading(true);
    try {
      const [rolesRes, modulesRes] = await Promise.all([getAdminRoles({ page: 0, size: 100 }), getAdminModules()]);
      if (rolesRes.success && rolesRes.data) setRoles(rolesRes.data.content);
      if (modulesRes.success && modulesRes.data) {
        setModules(modulesRes.data);
        setActiveModule(modulesRes.data.length > 0 ? modulesRes.data[0].module : null);
      }
    } catch {
      showAlert('error', 'Load failed', 'Could not load roles or modules.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryUsers = async () => {
    setCategoryLoading(true);
    try {
      const res = await getUsersByCategory(assignCategory, categorySearch);
      if (res.success && res.data) setCategoryUsers(res.data);
    } catch {
      showAlert('error', 'Load failed', `Could not load ${CATEGORY_LABELS[assignCategory].toLowerCase()}.`);
    } finally {
      setCategoryLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await getRoleAnalytics();
      if (res.success && res.data) setAnalytics(res.data);
    } catch {
      showAlert('error', 'Load failed', 'Could not load role analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadAnalyticsRoleUsers = async (roleId: string, searchText: string) => {
    setAnalyticsRoleLoading(true);
    try {
      const res = await getUsersByAdminRole(roleId, searchText);
      if (res.success && res.data) setAnalyticsRoleUsers(res.data);
    } catch {
      showAlert('error', 'Load failed', 'Could not load assigned users.');
    } finally {
      setAnalyticsRoleLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (activeTab === 'assign') {
      const timer = setTimeout(() => loadCategoryUsers(), 250);
      return () => clearTimeout(timer);
    }
  }, [activeTab, assignCategory, categorySearch]);

  useEffect(() => {
    if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab]);

  useEffect(() => {
    if (!analyticsRole) return;
    const timer = setTimeout(() => loadAnalyticsRoleUsers(analyticsRole.id, analyticsRoleSearch), 250);
    return () => clearTimeout(timer);
  }, [analyticsRole, analyticsRoleSearch]);

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)
    );
  }, [roles, search]);

  const activeRoles = useMemo(() => roles.filter((r) => r.status === 'ACTIVE'), [roles]);

  const openCreate = () => {
    setEditingRole(null);
    setForm({ ...emptyForm });
    setSelectedPrivileges(new Set());
    setActiveModule(modules.length > 0 ? modules[0].module : null);
    setFormMode('create');
    setAlert(null);
  };

  const openEdit = (role: AdminRole) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      description: role.description ?? '',
      status: role.status,
      privileges: role.privileges,
    });
    setSelectedPrivileges(new Set(role.privileges));
    setActiveModule(modules.length > 0 ? modules[0].module : null);
    setFormMode('edit');
    setAlert(null);
  };

  const closeForm = () => {
    setFormMode('list');
    setEditingRole(null);
  };

  const selectModule = (module: string) => {
    setActiveModule((prev) => (prev === module ? null : module));
  };

  const togglePrivilege = (token: string) => {
    setSelectedPrivileges((prev) => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  const toggleModuleAll = (module: string, actions: string[], checked: boolean) => {
    setSelectedPrivileges((prev) => {
      const next = new Set(prev);
      actions.forEach((action) => {
        const token = `${module}_${action}`;
        if (checked) next.add(token);
        else next.delete(token);
      });
      return next;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      showAlert('error', 'Incomplete', 'Role name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload: AdminRolePayload = {
        name: form.name.trim(),
        description: form.description?.trim(),
        status: form.status,
        privileges: Array.from(selectedPrivileges).sort(),
      };
      const res = editingRole
        ? await updateAdminRole(editingRole.id, payload)
        : await createAdminRole(payload);
      if (res.success) {
        showAlert('success', 'Saved', editingRole ? 'Role updated.' : 'Role created.');
        closeForm();
        await loadRoles();
        if (activeTab === 'analytics') await loadAnalytics();
      } else {
        showAlert('error', 'Save failed', res.message || 'Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this role? Assigned users will lose it.')) return;
    const res = await deleteAdminRole(id);
    if (res.success) {
      showAlert('success', 'Deleted', 'Role removed.');
      await loadRoles();
      await loadAnalytics();
    } else {
      showAlert('error', 'Delete failed', res.message || 'Please try again.');
    }
  };

  const handleAssign = async (userId: string, roleId: string) => {
    let res;
    if (!roleId) {
      res = await unassignAdminRole(userId);
    } else {
      res = await assignAdminRole({ userId, roleId });
    }
    if (res.success) {
      showAlert('success', roleId ? 'Assigned' : 'Unassigned', roleId ? 'Role assigned to user.' : 'Role removed from user.');
      await loadCategoryUsers();
      if (activeTab === 'analytics') await loadAnalytics();
      if (analyticsRole) await loadAnalyticsRoleUsers(analyticsRole.id, analyticsRoleSearch);
    } else {
      showAlert('error', roleId ? 'Assign failed' : 'Unassign failed', res.message || 'Please try again.');
    }
  };

  const displayName = (u: AdminUserReference) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name || u.email;
  };

  const renderUserRow = (u: AdminUserReference, showRoleBadge: boolean) => (
    <div
      key={u.id}
      className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/50 p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-slate-200">{displayName(u)}</span>
          {showRoleBadge && (
            <span className={`${badgeBase} bg-slate-700/30 text-slate-400`}>{u.role}</span>
          )}
          {u.adminRoleName && (
            <span className={`${badgeBase} bg-cyan-500/10 text-cyan-400`}>{u.adminRoleName}</span>
          )}
        </div>
        <div className="text-xs font-semibold text-slate-500">{u.email}</div>
      </div>
      <div className="flex items-center gap-2 sm:ml-4">
        <select
          value={u.adminRoleId ?? ''}
          onChange={(e) => handleAssign(u.id, e.target.value)}
          className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
        >
          <option value="">No admin role</option>
          {activeRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Role Assignment</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Create module-level privileges and assign them to candidates, recruiters or admins.
          </p>
        </div>
        {formMode === 'list' && (
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('roles')}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition ${
                activeTab === 'roles'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'border border-slate-800 bg-slate-900 text-slate-300 hover:border-cyan-500/40'
              }`}
            >
              <ShieldCheck size={15} />
              Roles
            </button>
            <button
              onClick={() => setActiveTab('assign')}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition ${
                activeTab === 'assign'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'border border-slate-800 bg-slate-900 text-slate-300 hover:border-cyan-500/40'
              }`}
            >
              <Users size={15} />
              Assign Users
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition ${
                activeTab === 'analytics'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'border border-slate-800 bg-slate-900 text-slate-300 hover:border-cyan-500/40'
              }`}
            >
              <BarChart3 size={15} />
              Analytics
            </button>
          </div>
        )}
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Full-page role form */}
      {formMode !== 'list' && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h2 className="text-lg font-black text-slate-100">
                  {editingRole ? 'Edit Role' : 'Create Role'}
                </h2>
                <p className="text-xs font-semibold text-slate-500">
                  Configure name, status and module-level privileges.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-sm font-black text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="role-form"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {editingRole ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>

          <form id="role-form" onSubmit={handleSave} className="space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="lg:col-span-1">
                  <label className={labelClass}>Role name</label>
                  <input
                    className={fieldClass}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Email Manager"
                  />
                </div>
                <div className="lg:col-span-1">
                  <label className={labelClass}>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as 'ACTIVE' | 'INACTIVE' }))}
                    className={`${fieldClass} appearance-none`}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className={labelClass}>Description</label>
                  <input
                    className={fieldClass}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="What this role is for"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Modules & Privileges</p>
                <p className="text-xs font-semibold text-slate-500">Select a module to manage its privileges</p>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="space-y-2 lg:col-span-1">
                  {modules.map((m) => {
                    const grantedCount = m.actions.filter((a) => selectedPrivileges.has(`${m.module}_${a}`)).length;
                    const isOpen = activeModule === m.module;
                    return (
                      <button
                        key={m.module}
                        type="button"
                        onClick={() => selectModule(m.module)}
                        className={`w-full rounded-xl border p-3 text-left transition ${
                          isOpen
                            ? 'border-cyan-500/50 bg-slate-800/60'
                            : 'border-slate-800 bg-slate-950/30 hover:border-cyan-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-slate-200">{m.label}</span>
                          {grantedCount > 0 && (
                            <span className="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] font-black text-cyan-400">
                              {grantedCount}/{m.actions.length}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                          {m.module}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-4 lg:col-span-2">
                  {(() => {
                    const m = modules.find((mod) => mod.module === activeModule);
                    if (!m) {
                      return (
                        <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 text-slate-500">
                          <ShieldCheck size={28} className="text-slate-700" />
                          <p className="text-sm font-semibold">Select a module to view privileges</p>
                        </div>
                      );
                    }
                    const allChecked = m.actions.every((a) => selectedPrivileges.has(`${m.module}_${a}`));
                    return (
                      <div>
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-sm font-black text-slate-100">{m.label}</h3>
                            <p className="text-xs font-semibold text-slate-500">{m.module}</p>
                          </div>
                          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 text-xs font-bold text-slate-300 transition hover:border-cyan-500/30">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) => toggleModuleAll(m.module, m.actions, e.target.checked)}
                              className="h-4 w-4 accent-cyan-500"
                            />
                            Select all actions
                          </label>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          {m.actions.map((action) => {
                            const token = `${m.module}_${action}`;
                            return (
                              <label
                                key={token}
                                className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5 transition hover:border-cyan-500/30"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPrivileges.has(token)}
                                  onChange={() => togglePrivilege(token)}
                                  className="h-4 w-4 accent-cyan-500"
                                />
                                <span className="text-xs font-bold text-slate-300">{action}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {editingRole ? 'Update Role' : 'Create Role'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-sm font-black text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Roles list */}
      {activeTab === 'roles' && formMode === 'list' && (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search roles…"
                className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
              />
            </div>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400"
            >
              <Plus size={16} />
              Create Role
            </button>
          </div>

          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm font-semibold text-slate-500">
              {search ? 'No roles match your search.' : 'No roles created yet.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 transition hover:border-cyan-500/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-bold text-slate-200">{role.name}</span>
                        {role.isSystem && (
                          <span className={`${badgeBase} bg-amber-500/10 text-amber-400`}>System</span>
                        )}
                        <span
                          className={`${badgeBase} ${
                            role.status === 'ACTIVE'
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-slate-700/30 text-slate-400'
                          }`}
                        >
                          {role.status}
                        </span>
                      </div>
                      {role.description && (
                        <p className="mt-1 truncate text-xs font-semibold text-slate-500">{role.description}</p>
                      )}
                      <p className="mt-1 text-xs font-semibold text-slate-600">
                        {role.privileges.length} privilege{role.privileges.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewRole(role)}
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-cyan-400"
                        aria-label="View privileges"
                      >
                        <Eye size={15} />
                      </button>
                      {!role.isSystem && (
                        <>
                          <button
                            onClick={() => openEdit(role)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-cyan-400"
                            aria-label="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => handleDelete(role.id)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-rose-400"
                            aria-label="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assign users */}
      {activeTab === 'assign' && formMode === 'list' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2">
              {(['CANDIDATE', 'RECRUITER', 'ADMIN'] as UserCategory[]).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setAssignCategory(cat)}
                  className={`rounded-xl px-4 py-2 text-sm font-black transition ${
                    assignCategory === cat
                      ? 'bg-cyan-500 text-slate-950'
                      : 'border border-slate-800 bg-slate-900 text-slate-300 hover:border-cyan-500/40'
                  }`}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
            <div className="relative max-w-md flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder={`Search ${CATEGORY_LABELS[assignCategory].toLowerCase()} by name or email…`}
                className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
              />
            </div>
          </div>

          {categoryLoading ? (
            <div className="flex min-h-[200px] items-center justify-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
          ) : categoryUsers.length === 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm font-semibold text-slate-500">
              {categorySearch
                ? `No ${CATEGORY_LABELS[assignCategory].toLowerCase()} match your search.`
                : `No ${CATEGORY_LABELS[assignCategory].toLowerCase()} found.`}
            </div>
          ) : (
            <div className="space-y-2">
              {categoryUsers.map((u) => renderUserRow(u, assignCategory === 'ADMIN'))}
            </div>
          )}
        </div>
      )}

      {/* Analytics */}
      {activeTab === 'analytics' && formMode === 'list' && (
        <div className="space-y-4">
          {analyticsLoading ? (
            <div className="flex min-h-[200px] items-center justify-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
          ) : analytics.length === 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm font-semibold text-slate-500">
              No roles found for analytics.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {analytics.map((item) => {
                const role = roles.find((r) => r.id === item.roleId);
                return (
                  <button
                    key={item.roleId}
                    type="button"
                    onClick={() => {
                      if (role) {
                        setAnalyticsRole(role);
                        setAnalyticsRoleSearch('');
                        loadAnalyticsRoleUsers(role.id, '');
                      }
                    }}
                    className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 text-left transition hover:border-cyan-500/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-200">{item.roleName}</p>
                        <p className="mt-1 text-2xl font-black text-cyan-400">{item.assignedUserCount}</p>
                        <p className="text-xs font-semibold text-slate-500">assigned user{item.assignedUserCount === 1 ? '' : 's'}</p>
                      </div>
                      <span
                        className={`${badgeBase} ${
                          item.status === 'ACTIVE'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-slate-700/30 text-slate-400'
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <div className="mt-3 text-xs font-semibold text-cyan-400">Click to view users →</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* View role privileges modal */}
      {viewRole && (
        <div className="fixed inset-0 z-[9950] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="mt-6 w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
              <ShieldCheck size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black text-slate-100">{viewRole.name}</h2>
              <button
                onClick={() => setViewRole(null)}
                className="ml-auto rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-auto p-4">
              {viewRole.description && (
                <p className="mb-3 text-xs font-semibold text-slate-500">{viewRole.description}</p>
              )}
              {modules.map((m) => {
                const granted = m.actions.filter((a) => viewRole.privileges.includes(`${m.module}_${a}`));
                if (granted.length === 0) return null;
                return (
                  <div key={m.module} className="mb-2 rounded-xl border border-slate-800 bg-slate-950/30 p-3">
                    <p className="text-xs font-bold text-slate-200">{m.label}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {granted.map((a) => (
                        <span key={a} className={`${badgeBase} bg-cyan-500/10 text-cyan-400`}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {viewRole.privileges.length === 0 && (
                <p className="text-sm font-semibold text-slate-500">No privileges granted.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analytics role users modal */}
      {analyticsRole && (
        <div className="fixed inset-0 z-[9950] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="mt-6 w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
              <Users size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black text-slate-100">{analyticsRole.name} — Assigned Users</h2>
              <button
                onClick={() => setAnalyticsRole(null)}
                className="ml-auto rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <div className="relative mb-4 max-w-md">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={analyticsRoleSearch}
                  onChange={(e) => setAnalyticsRoleSearch(e.target.value)}
                  placeholder="Search assigned users…"
                  className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
                />
              </div>

              {analyticsRoleLoading ? (
                <div className="flex min-h-[160px] items-center justify-center text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                </div>
              ) : analyticsRoleUsers.length === 0 ? (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm font-semibold text-slate-500">
                  {analyticsRoleSearch
                    ? 'No assigned users match your search.'
                    : 'No users are assigned to this role.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {analyticsRoleUsers.map((u) => renderUserRow(u, true))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleAssignmentComponent;
